import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import { useRouteMatch } from 'react-router';

import { useGetNotificationsQuery } from 'api/main';
import { texts } from 'utils/languages/fr';
import { ORDER_BY, sortDirection } from 'utils/constants';
import { useLocalStorage } from 'utils/hooks';
import { parseDate } from 'utils/helpers';

import {
    CustomTablePagination,
    StyledTableRow,
    ActionsMenu,
    EmptyTableBody,
    EnhancedTableHead,
} from 'components';

import { MenuMessageDetails } from 'components/ActionsMenu/actions';
import {
    Typography,
    TableCell,
    TableRow,
    Table,
    TableBody,
    TableContainer,
    TableFooter,
} from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles(theme => ({
    black: {
        color: theme.palette.common.black,
    },
}));

const tableHeads = [
    { id: ORDER_BY.type, isSortable: true, label: texts.USER_TYPE },
    { id: ORDER_BY.name, isSortable: true, label: texts.NOM },
    { id: ORDER_BY.createdAt, isSortable: true, label: texts.SENDING_DATE },
    { id: ORDER_BY.subject, isSortable: true, label: texts.MESSAGE_SUBJECT },
    { id: 'actions', isSortable: false, label: '' },
];

const getDefaultFilters = () => ({
    page: 1,
    orderBy: ORDER_BY.createdAt,
    direction: sortDirection.DESC,
});

const SentMessagesTable = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { path } = useRouteMatch();

    const [filters, setFilters] = useLocalStorage('sentMessagesFilter', getDefaultFilters());

    const {
        data: { notifications = [], lastPage } = { data: { notifications: [], lastPage: 1 } },
        isFetching: notificationsLoader,
    } = useGetNotificationsQuery(filters);

    const handlePageChange = useCallback(
        page => () => {
            setFilters(oldValue => ({ ...oldValue, page }));
        },
        [setFilters]
    );

    const handleSortMessages = useCallback(
        (property, direction) => {
            setFilters(oldValue => ({ ...oldValue, orderBy: property, direction }));
        },
        [setFilters]
    );

    const handleMessageDetails = useCallback(
        id => () => {
            dispatch(push(`${path}/details/${id}`));
        },
        [dispatch, path]
    );

    const renderNotification = ({ 0: { id, createdAt, subject } = {}, name, type }) => {
        const MessageDetailsItem = React.forwardRef((propd, ref) => (
            <MenuMessageDetails ref={ref} {...propd} />
        ));
        return (
            <StyledTableRow key={id}>
                <TableCell>{type}</TableCell>
                <TableCell className={classes.black}>{name}</TableCell>
                <TableCell>{parseDate(createdAt)}</TableCell>
                <TableCell>{subject}</TableCell>
                <TableCell>
                    <ActionsMenu>
                        <MessageDetailsItem onClick={handleMessageDetails(id)} />
                    </ActionsMenu>
                </TableCell>
            </StyledTableRow>
        );
    };

    return (
        <React.Fragment>
            <Typography variant="h5" align="left" gutterBottom>
                {texts.SENT_MESSAGES}
            </Typography>
            <TableContainer>
                <Table style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                        <col width="15%" />
                        <col width="25%" />
                        <col width="15%" />
                        <col width="35%" />
                        <col width="10%" />
                    </colgroup>
                    <EnhancedTableHead
                        headCells={tableHeads}
                        orderBy={filters.orderBy}
                        direction={filters.direction}
                        handleSort={handleSortMessages}
                    />
                    {notificationsLoader || notifications.length === 0 ? (
                        <EmptyTableBody colSpan={tableHeads.length} loading={notificationsLoader} />
                    ) : (
                        <TableBody>{notifications.map(renderNotification)}</TableBody>
                    )}
                    <TableFooter>
                        <TableRow>
                            <CustomTablePagination
                                currentPage={filters.page}
                                lastPage={lastPage}
                                changePage={handlePageChange}
                                columns={tableHeads.length}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </React.Fragment>
    );
};

export default SentMessagesTable;
